import { ArrowRight, Star } from "lucide-react";
import { SITE } from "../data/site";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

const PACKAGES = [
    {
        id: "plan-distanta",
        name: "Plan la distanță",
        price: "450",
        unit: "lei / pachet",
        description: "Totul pus în ordine, de departe.",
        features: ["Discuție cu familia", "Listă logistică, calendar și buget", "Până la 3 ore de muncă"],
    },
    {
        id: "spital-acasa",
        name: "Din spital acasă",
        price: "900",
        unit: "lei / pachet",
        description: "Întoarcerea acasă, fără haos.",
        featured: true,
        features: ["Verificarea logistică a locuinței", "Oferte, programări și coordonare transport", "Revenire după sosire · până la 6 ore"],
    },
    {
        id: "familia-organizata",
        name: "Familia organizată",
        price: "350",
        unit: "lei / lună",
        description: "Liniștea copilului care e departe.",
        features: ["Până la 2 ore de coordonare lunară", "Maximum 4 solicitări simple", "Răspuns în timpul programului"],
    },
    {
        id: "ajutor-programat",
        name: "Ajutor programat",
        price: "1.680",
        unit: "lei / lună",
        description: "Ritm constant, om cunoscut.",
        features: ["16 ore pe lună de gospodărie și sarcini", "Aceeași persoană de fiecare dată", "Plan agreat dinainte"],
    },
    {
        id: "ajutor-temporar",
        name: "Ajutor temporar",
        price: "120",
        unit: "lei / oră",
        description: "Exact cât trebuie, când trebuie.",
        features: ["Minimum 2 ore pe vizită", "Ofertă pe durata necesară", "Ideal în perioada de recuperare"],
    },
];

const Card = ({ pkg, delay }) => (
    <Reveal delay={delay} className="h-full">
        <div
            data-testid={`price-card-${pkg.id}`}
            className={`flex h-full flex-col rounded-3xl p-7 sm:p-8 transition-shadow duration-500 hover:shadow-xl ${
                pkg.featured
                    ? "bg-coral text-cream shadow-lg shadow-coral/25 hover:shadow-coral/30"
                    : "bg-creamlight ring-1 ring-teal/10 hover:shadow-teal/10"
            }`}
        >
            {pkg.featured && (
                <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-cream/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="price-card-badge">
                    <Star className="h-3 w-3" /> Cel mai ales
                </span>
            )}
            <h3 className={`font-serif text-2xl font-semibold ${pkg.featured ? "text-cream" : "text-charcoal"}`}>{pkg.name}</h3>
            <p className={`mt-1 text-sm ${pkg.featured ? "text-cream/80" : "text-charcoal/60"}`}>{pkg.description}</p>
            <p className="mt-5 flex items-baseline gap-2">
                <span className={`font-serif text-5xl font-semibold tracking-tight ${pkg.featured ? "text-cream" : "text-teal"}`}>{pkg.price}</span>
                <span className={`font-mono text-xs uppercase tracking-wider ${pkg.featured ? "text-cream/75" : "text-charcoal/55"}`}>{pkg.unit}</span>
            </p>
            <ul className="mt-6 flex flex-col gap-3 flex-1">
                {pkg.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm leading-relaxed ${pkg.featured ? "text-cream/90" : "text-charcoal/70"}`}>
                        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${pkg.featured ? "bg-cream" : "bg-coral"}`} />
                        {f}
                    </li>
                ))}
            </ul>
            <a
                href={SITE.phoneHref}
                data-testid={`price-cta-${pkg.id}`}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-colors duration-300 ${
                    pkg.featured
                        ? "bg-cream text-coral-dark hover:bg-white"
                        : "bg-teal text-cream hover:bg-teal-dark"
                }`}
            >
                Sună pentru pachet
                <ArrowRight className="h-4 w-4" />
            </a>
        </div>
    </Reveal>
);

export const Pricing = () => (
    <section id="preturi" data-testid="pricing-section" className="bg-sand/50 py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                testid="pricing-heading"
                eyebrow="Prețuri & pachete"
                title="Tarife clare, spuse din start"
                text="Alegi pachetul, îl punem în scris și ne ținem de el. Fără costuri ascunse și fără promisiuni nelimitate."
            />
            <div className="mt-12 grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {PACKAGES.map((pkg, i) => (
                    <Card key={pkg.id} pkg={pkg} delay={(i % 3) * 0.08} />
                ))}
                <Reveal delay={0.16} className="h-full">
                    <div className="flex h-full flex-col justify-between rounded-3xl bg-teal p-7 sm:p-8 text-cream" data-testid="price-note-card">
                        <div>
                            <h3 className="font-serif text-2xl font-semibold">Bine de știut</h3>
                            <p className="mt-4 text-sm leading-relaxed text-cream/85">
                                Bunurile, transportul, echipamentele și terapeuții se plătesc separat, direct furnizorilor. Orele suplimentare se ofertează înainte, orientativ 150 lei/oră de coordonare.
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-cream/85">
                                Deplasările din afara zonei se cotează separat. Un pachet nu se cumulează cu abonamentul pentru aceeași muncă.
                            </p>
                        </div>
                        <a href={SITE.phoneHref} data-testid="price-note-call" className="mt-7 inline-flex items-center gap-2 font-semibold text-cream link-underline w-fit">
                            Întreabă-ne direct
                            <ArrowRight className="h-4 w-4" />
                        </a>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);
